import Swiper from 'swiper/bundle';
import { accessiblePagination, bindPaginationButtons } from '../lib/swiper-pagination.js';
import { onReady } from '../lib/on-ready.js';

const DEFAULT_ACTIVE_INDEX = 1;
const PRELOAD_RADIUS = 3;

const BREAKPOINTS = {
    md: 768,
    lg: 992,
};

// Match _articlecarousel.scss clamp() min/max and former % widths.
const SLIDE_WIDTHS = {
    default: { ratio: 0.68, min: 224, max: 320 },
    md: { ratio: 0.44, min: 256, max: 384 },
    lg: { ratio: 0.38, min: 288, max: 448 },
};

const ArticleCarousel = {
    getInitialSlide(slideCount) {
        // Start on image 2 when possible so image 1 shows left and the rest peek right.
        return slideCount > 1 ? DEFAULT_ACTIVE_INDEX : 0;
    },

    getSlideWidthConfig() {
        const viewportWidth = window.innerWidth;

        if (viewportWidth >= BREAKPOINTS.lg) {
            return SLIDE_WIDTHS.lg;
        }

        if (viewportWidth >= BREAKPOINTS.md) {
            return SLIDE_WIDTHS.md;
        }

        return SLIDE_WIDTHS.default;
    },

    setSlideWidths(container) {
        const { ratio, min, max } = ArticleCarousel.getSlideWidthConfig();
        const width = Math.min(max, Math.max(min, Math.round(container.offsetWidth * ratio)));

        container.querySelectorAll('.swiper-slide').forEach((slide) => {
            slide.style.width = `${width}px`;
        });
    },

    loadSlideImage(slide, swiper) {
        const image = slide.querySelector('.article-carousel-image');

        if (!image || image.dataset.loaded === 'true') {
            return false;
        }

        const deferredSrc = image.dataset.src;

        if (!deferredSrc) {
            image.dataset.loaded = 'true';
            return false;
        }

        image.src = deferredSrc;
        image.removeAttribute('data-src');
        image.dataset.loaded = 'true';

        if (!image.complete) {
            image.addEventListener('load', () => ArticleCarousel.updateLayout(swiper), { once: true });
        }

        return true;
    },

    preloadNearbyImages(swiper, radius = PRELOAD_RADIUS) {
        const activeIndex = swiper.activeIndex;
        let loadedAny = false;

        swiper.slides.forEach((slide, index) => {
            if (Math.abs(index - activeIndex) > radius) {
                return;
            }

            if (ArticleCarousel.loadSlideImage(slide, swiper)) {
                loadedAny = true;
            }
        });

        if (loadedAny) {
            ArticleCarousel.updateLayout(swiper);
        }
    },

    updateLayout(swiper) {
        const activeIndex = swiper.activeIndex;

        requestAnimationFrame(() => {
            ArticleCarousel.setSlideWidths(swiper.el);
            swiper.update();

            let maxHeight = 0;

            swiper.slides.forEach((slide) => {
                maxHeight = Math.max(maxHeight, slide.offsetHeight);
            });

            if (maxHeight > 0) {
                swiper.wrapperEl.style.minHeight = `${maxHeight}px`;
            }

            swiper.slideTo(activeIndex, 0, false);
        });
    },

    bindImageLoadSync(container, swiper) {
        container.querySelectorAll('.article-carousel-image').forEach((image) => {
            if (image.dataset.loaded !== 'true' || image.complete) {
                return;
            }

            image.addEventListener('load', () => ArticleCarousel.updateLayout(swiper), { once: true });
        });
    },

    init() {
        document.querySelectorAll('.swiper-article-carousel').forEach((container) => {
            if (container.swiper) {
                return;
            }

            const slideCount = parseInt(container.dataset.slideCount, 10) || 0;
            const initialSlide = ArticleCarousel.getInitialSlide(slideCount);

            // Safari mis-resolves CSS %/vw widths inside Swiper's 3D-transformed wrapper.
            // Set explicit pixel widths before init so coverflow centering is correct.
            ArticleCarousel.setSlideWidths(container);

            const swiper = new Swiper(container, {
                effect: 'coverflow',
                grabCursor: true,
                centeredSlides: true,
                slidesPerView: 'auto',
                spaceBetween: 8,
                initialSlide,
                loop: false,
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                navigation: {
                    nextEl: container.querySelector('.swiper-button-next'),
                    prevEl: container.querySelector('.swiper-button-prev'),
                },
                pagination: {
                    el: container.querySelector('.swiper-pagination'),
                    ...accessiblePagination,
                },
                coverflowEffect: {
                    rotate: 38,
                    stretch: -12,
                    depth: 120,
                    modifier: 1,
                    slideShadows: false,
                },
                a11y: {
                    enabled: true,
                    slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
                },
                on: {
                    init(swiperInstance) {
                        if (slideCount > 1 && swiperInstance.activeIndex !== DEFAULT_ACTIVE_INDEX) {
                            swiperInstance.slideTo(DEFAULT_ACTIVE_INDEX, 0);
                        }

                        bindPaginationButtons(swiperInstance);
                        ArticleCarousel.preloadNearbyImages(swiperInstance);
                        ArticleCarousel.bindImageLoadSync(container, swiperInstance);
                        ArticleCarousel.updateLayout(swiperInstance);
                        requestAnimationFrame(() => ArticleCarousel.updateLayout(swiperInstance));
                    },
                    resize(swiperInstance) {
                        ArticleCarousel.updateLayout(swiperInstance);
                    },
                    slideChangeTransitionStart(swiperInstance) {
                        ArticleCarousel.preloadNearbyImages(swiperInstance);
                    },
                    slideChange(swiperInstance) {
                        bindPaginationButtons(swiperInstance);
                        ArticleCarousel.preloadNearbyImages(swiperInstance);
                    },
                    paginationUpdate(swiperInstance) {
                        bindPaginationButtons(swiperInstance);
                    },
                },
            });
        });
    },
};

onReady(() => ArticleCarousel.init());
